import { cn } from '@/lib/utils';
import { type WorkOrder } from '@/types';
import { User } from 'lucide-react';
import { useState } from 'react';
import { EditContactDialog } from '@/components/features/WorkOrders/modals/EditContactDialog';

interface InlineContactCellProps {
    workOrder: WorkOrder;
    onUpdate?: (data: {
        contact_first_name: string;
        contact_last_name: string;
        phone?: string;
        email?: string;
        service_address: string;
        service_address_lat?: number;
        service_address_lng?: number;
        service_address_street?: string;
        service_address_house_number?: string;
        service_address_city?: string;
        service_address_postal_code?: string;
        service_address_place_id?: string;
    }) => void;
    className?: string;
    readOnly?: boolean;
}

export function InlineContactCell({ workOrder, onUpdate, className, readOnly = false }: InlineContactCellProps) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const contactName = workOrder.full_contact_name || `${workOrder.contact_first_name} ${workOrder.contact_last_name}`.trim() || 'No contact';

    const handleSave = async (data: {
        contact_first_name: string;
        contact_last_name: string;
        phone?: string;
        email?: string;
        service_address: string;
        service_address_lat?: number;
        service_address_lng?: number;
        service_address_street?: string;
        service_address_house_number?: string;
        service_address_city?: string;
        service_address_postal_code?: string;
        service_address_place_id?: string;
    }) => {
        try {
            await onUpdate?.(data);
            setIsDialogOpen(false);
        } catch (error) {
            console.error('Failed to update contact:', error);
            // Keep dialog open on error so user can retry
        }
    };

    // Read-only mode: just display the contact name
    if (readOnly || !onUpdate) {
        return (
            <div
                className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm',
                    !contactName || contactName === 'No contact' ? 'text-muted-foreground' : '',
                    className,
                )}
            >
                <User className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="max-w-[150px] truncate">{contactName}</span>
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsDialogOpen(true);
                }}
                className={cn(
                    'hover:bg-accent w-full rounded px-2 py-1.5 text-left transition-colors',
                    'flex items-center gap-2 text-sm',
                    !contactName || contactName === 'No contact' ? 'text-muted-foreground' : '',
                    className,
                )}
            >
                <User className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="max-w-[150px] truncate">{contactName}</span>
            </button>

            <EditContactDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                initialData={{
                    contact_first_name: workOrder.contact_first_name,
                    contact_last_name: workOrder.contact_last_name,
                    phone: workOrder.phone,
                    email: workOrder.email,
                }}
                onSave={(data) => handleSave({ ...data, service_address: workOrder.service_address || '' })}
            />
        </>
    );
}
